import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import {
  CircularProgress,
  Pagination,
  TablePagination,
  Avatar,
  Button,
  IconButton,
} from "@mui/material";
import { useSnackbar } from "notistack";
import moment from "moment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Label from "src/components/Label";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import MUICustomTabs from "src/components/GeneralComponents/MUICustomTabs";
import QuestionsListing from "src/pages/UserForms/QuestionsListing";
import FeedBack from "src/pages/UserForms/FeedBack";
import GeneralPopUpModel from "src/components/GeneralComponents/GeneralPopUpModel";
import GeneralCkeditor from "src/components/GeneralCkeditor";
import {
  deleteForm,
  getAllFormsList_members_and_coach,
  addFeedback,
  editFeedback,
  getFormMembersDetails_V1,
  getTemplateDetail_V1,
} from "src/DAL/SendFormTemplate/sendFormTemplate";
import { s3baseUrl } from "src/config/config";

const bottom_in_chat = {
  hidden: {
    y: "100px",
    x: "0px",
    height: "0",
    opacity: 1,
    scale: 1,
  },
  visible: {
    opacity: 1,
    scale: 1,
    height: "67vh",
    y: "0px",
    x: "0px",
    transition: { delay: 0, duration: 0.5 },
  },
};

const MODAL_Z_INDEX_SX = { zIndex: 10001 };

// ─── Preview (DetailsFormInformation) embedded ───────────────────────────────
function FormPreview({ formItem, memberId, onBack }) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [feedbackList, setFeedbackList] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [openAddFeedback, setOpenAddFeedback] = useState(false);
  const [feedback, setFeedback] = useState({ feedback: "", feedbackId: "" });

  const formId = formItem?._id;
  const page_type = "members_forms";

  const getFormMembersDetails = useCallback(async () => {
    if (!formId || !memberId) return;
    setIsLoading(true);
    const result = await getFormMembersDetails_V1(formId, memberId);
    if (result.code === 200) {
      setQuestions(result?.data?.questions || []);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
    setIsLoading(false);
  }, [formId, memberId]);

  const getFormDetails = useCallback(async () => {
    if (!formId || !memberId) return;
    const result = await getTemplateDetail_V1(formId, memberId, "individual");
    if (result.code === 200) {
      setFeedbackList(result?.data || []);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  }, [formId, memberId]);

  const handleAddFeedback = async () => {
    setIsFeedbackLoading(true);
    setOpenFeedback(false);
    const postData = {
      form_id: formId,
      member_id: memberId,
      feedback_content: feedback?.feedback,
      feedback_type: "individual",
    };
    const EditPost = {
      feedback_content: feedback?.feedback,
      feedback_id: feedback?.feedbackId,
    };
    const result = feedback?.feedbackId
      ? await editFeedback(EditPost)
      : await addFeedback(postData);
    if (result.code === 200) {
      enqueueSnackbar(result.message, { variant: "success" });
      setOpenAddFeedback(false);
      setFeedback({ feedback: "", feedbackId: "" });
      if (!feedback?.feedbackId) {
        setFeedbackList([result?.feedback, ...feedbackList]);
      } else {
        setFeedbackList(
          feedbackList.map((item) =>
            item._id === feedback?.feedbackId ? result?.feedback : item,
          ),
        );
      }
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
    setIsFeedbackLoading(false);
  };

  const TABS_OPTIONS = [
    {
      title: "Questions",
      component: (
        <QuestionsListing questions={questions} setQuestions={setQuestions} />
      ),
    },
    {
      title: "Feedback",
      component: (
        <FeedBack
          feedbackList={feedbackList}
          setFeedbackList={setFeedbackList}
          setOpenAddFeedback={setOpenAddFeedback}
          setFeedback={setFeedback}
          page_type={page_type}
          dialogSx={MODAL_Z_INDEX_SX}
        />
      ),
    },
  ];

  useEffect(() => {
    getFormMembersDetails();
    getFormDetails();
  }, [formId, memberId]);

  return (
    <>
      <CustomConfirmation
        openDelete={openFeedback}
        setOpenDelete={setOpenFeedback}
        title="Your feedback will be sent to multiple users. Are you sure you want to proceed?"
        handleAgree={handleAddFeedback}
        sx={MODAL_Z_INDEX_SX}
      />

      {/* Preview header */}
      <div className="d-flex align-items-center justify-content-between px-2 pt-2 pb-1">
        <div className="d-flex align-items-center gap-1">
          <IconButton size="small" onClick={onBack}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            {formItem?.title || "Form Preview"}
          </span>
        </div>
        {tabValue === 1 && (
          <button
            className="small-contained-button"
            style={{ height: 36, padding: "0 16px", fontSize: 13 }}
            onClick={() => {
              setFeedback({ feedback: "", feedbackId: "" });
              setOpenAddFeedback(true);
            }}
          >
            Add Feedback
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <CircularProgress size={28} />
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: "auto", padding: "0 8px 8px" }}>
          <MUICustomTabs
            data={TABS_OPTIONS}
            value={tabValue}
            handleChange={(e, v) => setTabValue(v)}
          />
        </div>
      )}

      <GeneralPopUpModel
        open={openAddFeedback}
        setOpen={setOpenAddFeedback}
        title={`${feedback?.feedbackId ? "Edit" : "Add"} Feedback`}
        sx={MODAL_Z_INDEX_SX}
        componentToPassDown={
          <>
            <GeneralCkeditor
              setInputs={setFeedback}
              inputs={feedback}
              name="feedback"
              editorHeight={250}
            />
            <div className="text-end mt-2">
              <Button variant="contained" onClick={handleAddFeedback}>
                {isFeedbackLoading ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </>
        }
      />
    </>
  );
}

// ─── Main FormsListBox ────────────────────────────────────────────────────────
function FormsListBox({ isOpen, handleClose, member }) {
  const { enqueueSnackbar } = useSnackbar();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [deleteDoc, setDeleteDoc] = useState("");
  const [openDelete, setOpenDelete] = useState(false);

  // preview state
  const [previewItem, setPreviewItem] = useState(null);

  const getFormsList = useCallback(
    async (search = "") => {
      if (!member?._id) return;
      setIsLoading(true);
      const result = await getAllFormsList_members_and_coach(
        page,
        rowsPerPage,
        { search },
        member._id,
      );
      if (result.code === 200) {
        const data = result.forms.map((item) => ({
          ...item,
          created_at: item.createdAt
            ? moment(item.createdAt).format("DD MMM YYYY")
            : "N/A",
        }));
        setUserList(data);
        setTotalCount(result.total_forms);
        setTotalPages(result.total_pages);
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
      }
      setIsLoading(false);
    },
    [member?._id, page, rowsPerPage],
  );

  const handleAgreeDelete = (item) => {
    setDeleteDoc(item);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    setOpenDelete(false);
    setIsLoading(true);
    const result = await deleteForm(deleteDoc.form_slug);
    if (result.code === 200) {
      enqueueSnackbar(result.message, { variant: "success" });
      getFormsList(searchText);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setPageCount(newPage <= 0 ? 1 : newPage + 1);
  };

  const handleChangePages = (event, newPage) => {
    if (newPage <= 0) {
      setPage(0);
      setPageCount(1);
    } else {
      setPage(newPage - 1);
      setPageCount(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setPageCount(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    setPageCount(1);
    getFormsList(searchText);
  };

  // reset when member changes or modal opens
  useEffect(() => {
    if (isOpen && member?._id) {
      setIsExpanded(true);
      setPage(0);
      setPageCount(1);
      setSearchText("");
      setPreviewItem(null);
    }
  }, [isOpen, member?._id]);

  useEffect(() => {
    if (isOpen && member?._id) {
      getFormsList(searchText);
    }
  }, [page, rowsPerPage, isOpen, member?._id]);

  const fullName = member
    ? `${member.first_name || ""} ${member.last_name || ""}`.trim()
    : "Member";

  return (
    <>
      <CustomConfirmation
        open={openDelete}
        setOpen={setOpenDelete}
        title="Are you sure you want to delete?"
        handleAgree={handleDelete}
        sx={MODAL_Z_INDEX_SX}
      />

      {/* ── Header ── */}
      <div
        className="user-chat-box-header d-flex align-items-center justify-content-between"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: "pointer" }}
      >
        <div className="d-flex align-items-center gap-2">
          {member?.profile_image && (
            <Avatar
              src={s3baseUrl + member.profile_image}
              sx={{ width: 28, height: 28 }}
            />
          )}
          <div className="user-box-heading ms-1">
            {previewItem ? `Preview — ${fullName}` : `Forms — ${fullName}`}
          </div>
        </div>
        <div className="d-flex align-items-center">
          {isExpanded ? (
            <Icon icon="akar-icons:chevron-down" />
          ) : (
            <Icon icon="akar-icons:chevron-up" />
          )}
          <Icon
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            className="ms-2 chat-box-cross-icon"
            icon="charm:cross"
            style={{ cursor: "pointer" }}
          />
        </div>
      </div>

      {/* ── Body ── */}
      <motion.div
        variants={bottom_in_chat}
        initial="hidden"
        animate={isExpanded ? "visible" : "hidden"}
        exit="hidden"
        style={{
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {previewItem ? (
          // ── Preview view ──
          <div
            className="d-flex flex-column h-100"
            style={{ overflow: "hidden" }}
          >
            <FormPreview
              formItem={previewItem}
              memberId={member?._id}
              onBack={() => setPreviewItem(null)}
            />
          </div>
        ) : (
          // ── List view ──
          <div
            className="d-flex flex-column h-100"
            style={{ overflow: "hidden" }}
          >
            {/* Search + Pagination row */}
            <div className="px-2 pt-2 pb-1">
              <form onSubmit={handleSearch} style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  placeholder="Search forms..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{
                    flex: 1,
                    height: 36,
                    padding: "0 12px",
                    border: "1px solid #ccc",
                    borderRadius: 6,
                    fontSize: 13,
                    outline: "none",
                    backgroundColor: "var(--background-secondary-color, #fff)",
                    color: "inherit",
                  }}
                />
                <button
                  type="submit"
                  className="small-contained-button"
                  style={{
                    height: 36,
                    padding: "0 16px",
                    fontSize: 13,
                    whiteSpace: "nowrap",
                  }}
                >
                  Search
                </button>
              </form>

              {/* Pagination */}
              <div
                className="d-flex align-items-center justify-content-between"
                style={{ marginTop: 4 }}
              >
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={() => null}
                  sx={{
                    "& .MuiTablePagination-toolbar": {
                      minHeight: 32,
                      paddingLeft: 0,
                    },
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                      { fontSize: 12 },
                  }}
                />
                <Pagination
                  count={totalPages + 1}
                  page={pageCount}
                  defaultPage={0}
                  onChange={handleChangePages}
                  size="small"
                  className="pagination-style"
                />
              </div>
            </div>

            {/* List */}
            <div style={{ flex: 1, overflowY: "auto", padding: "0 8px 8px" }}>
              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center mt-4">
                  <CircularProgress size={28} />
                </div>
              ) : userList.length === 0 ? (
                <div className="text-center text-muted mt-4">
                  No forms found
                </div>
              ) : (
                userList.map((item, index) => (
                  <div
                    key={item._id || index}
                    className="d-flex align-items-center justify-content-between p-2 mb-1"
                    style={{
                      borderRadius: 6,
                      border: "1px solid #e0e0e0",
                      background: "#fafafa",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        className="fw-semibold text-truncate"
                        style={{ fontSize: 13 }}
                      >
                        {item.title || "Untitled"}
                      </div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        {item.created_at}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 ms-2 flex-wrap justify-content-end">
                      <Label
                        variant="ghost"
                        color={
                          item.submission_status === "submitted"
                            ? "success"
                            : "warning"
                        }
                      >
                        {item.submission_status === "submitted"
                          ? "Submitted"
                          : "Pending"}
                      </Label>
                      <Label
                        variant="ghost"
                        color={item.status === false ? "error" : "success"}
                      >
                        {item.status === false ? "InActive" : "Active"}
                      </Label>
                      <button
                        className="small-contained-button"
                        style={{ padding: "2px 10px", fontSize: 12 }}
                        onClick={() => setPreviewItem(item)}
                      >
                        Preview
                      </button>
                      {item.submission_status === "submitted" && (
                        <Icon
                          icon="ant-design:delete-twotone"
                          style={{
                            cursor: "pointer",
                            color: "#d32f2f",
                            fontSize: 18,
                          }}
                          onClick={() => handleAgreeDelete(item)}
                        />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </motion.div>
    </>
  );
}

export default FormsListBox;
